// The host a decker's token is in (issue 1532; docs/MRCS/shadowrun-3.md §9 step 18): the DM's alone. The DM sets it
// on the decker's token (`object.matrix`) — its security code, its Security Value, each subsystem's rating, its alert
// and whether its ratings are shown to the decker's players — and the server keeps the decker's security tally beside
// it, adding the host's successes after every System Test. No player is ever sent any of it (`projectObject` builds a
// player's slice without it); a System Test's record reaches a player without the host's numbers unless `shown`.
//
// Pure, and importing nothing, so the state's reader can use it without pulling the rules in.
/** The most a host's numbers reach. */
export const HOST_LIMITS = { name: 80, security: 30, rating: 40, tally: 999, ratings: 8, steps: 24 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
/** A host off the wire or the store: exact shape or null. Untrusted input, always. */
export function parseMatrixHost(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['name', 'code', 'security', 'ratings', 'alert', 'shown', 'sheaf', 'sheafOff'].includes(k)))
        return null;
    let sheaf = null;
    if (v.sheaf !== undefined) {
        sheaf = parseSheaf(v.sheaf);
        if (!sheaf)
            return null;
    }
    if (v.sheafOff !== undefined && v.sheafOff !== true)
        return null;
    if (v.name !== undefined && (typeof v.name !== 'string' || !v.name.trim() || v.name.length > HOST_LIMITS.name || LINE_BAD.test(v.name)))
        return null;
    if (typeof v.code !== 'string' || !KEY_RE.test(v.code) || !isInt(v.security, 1, HOST_LIMITS.security) || !isObj(v.ratings))
        return null;
    const entries = Object.entries(v.ratings);
    if (!entries.length || entries.length > HOST_LIMITS.ratings || entries.some(([k, r]) => !KEY_RE.test(k) || !isInt(r, 1, HOST_LIMITS.rating)))
        return null;
    if (!(v.alert === null || (typeof v.alert === 'string' && KEY_RE.test(v.alert))) || typeof v.shown !== 'boolean')
        return null;
    return {
        ...(typeof v.name === 'string' ? { name: v.name.trim() } : {}), code: v.code, security: v.security,
        ratings: Object.fromEntries(entries), alert: v.alert, shown: v.shown,
        ...(sheaf?.length ? { sheaf } : {}), ...(v.sheafOff === true ? { sheafOff: true } : {}),
    };
}
const IC_REF_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
/** A sheaf off the wire or the store: each step exact, an IC with its rating or an alert or both; null when malformed. */
export function parseSheaf(v) {
    if (!Array.isArray(v) || v.length > HOST_LIMITS.steps)
        return null;
    const out = [];
    for (const e of v) {
        if (!isObj(e) || Object.keys(e).some((k) => !['step', 'ic', 'rating', 'alert'].includes(k)) || !isInt(e.step, 1, HOST_LIMITS.tally))
            return null;
        if (e.ic !== undefined && (typeof e.ic !== 'string' || !IC_REF_RE.test(e.ic) || !isInt(e.rating, 1, HOST_LIMITS.rating)))
            return null;
        if (e.ic === undefined && e.rating !== undefined)
            return null;
        if (e.alert !== undefined && (typeof e.alert !== 'string' || !KEY_RE.test(e.alert)))
            return null;
        if (e.ic === undefined && e.alert === undefined)
            return null;
        out.push({ step: e.step, ...(typeof e.ic === 'string' ? { ic: e.ic, rating: e.rating } : {}), ...(typeof e.alert === 'string' ? { alert: e.alert } : {}) });
    }
    return out;
}
/** A token's host and tally off the wire or the store: exact shape or null. */
export function parseMatrixState(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['host', 'tally', 'fired', 'ic', 'persona', 'mpcpLost', 'lowered'].includes(k)) || !isInt(v.tally, 0, HOST_LIMITS.tally))
        return null;
    if ((v.persona !== undefined && !isInt(v.persona, 1, 99)) || (v.mpcpLost !== undefined && !isInt(v.mpcpLost, 1, 99)))
        return null;
    if (v.lowered !== undefined && (!isObj(v.lowered) || Object.entries(v.lowered).some(([k, n]) => !KEY_RE.test(k) || !isInt(n, 1, 99)) || Object.keys(v.lowered).length > 8))
        return null;
    const host = parseMatrixHost(v.host);
    if (!host)
        return null;
    let fired = null;
    if (v.fired !== undefined) {
        const n = host.sheaf?.length ?? 0;
        if (!Array.isArray(v.fired) || !v.fired.every((i) => isInt(i, 0, n - 1)) || new Set(v.fired).size !== v.fired.length)
            return null;
        fired = [...v.fired].sort((a, b) => a - b);
    }
    const ic = [];
    if (v.ic !== undefined) {
        if (!Array.isArray(v.ic) || v.ic.length > HOST_LIMITS.steps)
            return null;
        for (const x of v.ic) {
            if (!isObj(x) || Object.keys(x).some((k) => !['name', 'rating', 'combatant', 'every', 'ref', 'damage', 'met', 'holds'].includes(k)))
                return null;
            if ((x.ref !== undefined && (typeof x.ref !== 'string' || !IC_REF_RE.test(x.ref))) || (x.damage !== undefined && !isInt(x.damage, 1, 99)) || (x.met !== undefined && x.met !== true) || (x.holds !== undefined && x.holds !== true))
                return null;
            if (typeof x.name !== 'string' || !x.name.trim() || x.name.length > HOST_LIMITS.name || LINE_BAD.test(x.name) || !isInt(x.rating, 1, HOST_LIMITS.rating))
                return null;
            if (typeof x.combatant !== 'string' || !UUID_RE.test(x.combatant) || (x.every !== undefined && x.every !== true))
                return null;
            ic.push({ name: x.name.trim(), rating: x.rating, combatant: x.combatant, ...(x.every ? { every: true } : {}), ...(typeof x.ref === 'string' ? { ref: x.ref } : {}), ...(typeof x.damage === 'number' ? { damage: x.damage } : {}), ...(x.met ? { met: true } : {}), ...(x.holds ? { holds: true } : {}) });
        }
    }
    return {
        host, tally: v.tally, ...(fired?.length ? { fired } : {}), ...(ic.length ? { ic } : {}),
        ...(typeof v.persona === 'number' ? { persona: v.persona } : {}), ...(typeof v.mpcpLost === 'number' ? { mpcpLost: v.mpcpLost } : {}),
        ...(isObj(v.lowered) && Object.keys(v.lowered).length ? { lowered: { ...v.lowered } } : {}),
    };
}
export const copyMatrixState = (m) => ({
    host: { ...m.host, ratings: { ...m.host.ratings }, ...(m.host.sheaf ? { sheaf: m.host.sheaf.map((x) => ({ ...x })) } : {}) },
    tally: m.tally, ...(m.fired ? { fired: [...m.fired] } : {}), ...(m.ic ? { ic: m.ic.map((x) => ({ ...x })) } : {}),
    ...(m.persona ? { persona: m.persona } : {}), ...(m.mpcpLost ? { mpcpLost: m.mpcpLost } : {}), ...(m.lowered ? { lowered: { ...m.lowered } } : {}),
});
export function matrixSeen(m) {
    const ic = (m.ic ?? []).filter((x) => x.met).map((x) => ({ name: x.name, rating: x.rating, combatant: x.combatant, damage: x.damage ?? 0 }));
    return ic.length || m.persona ? { ic, persona: m.persona ?? 0 } : null;
}
/**
 * The sheaf's steps that fire now (qa-runs#1173; p. 211): every step at or below the tally not fired yet, all at once,
 * in the sheaf's order by step. None when the DM turned the sheaf off.
 */
export function sheafDue(m) {
    if (!m.host.sheaf || m.host.sheafOff)
        return [];
    const fired = new Set(m.fired ?? []);
    return m.host.sheaf.map((s, i) => ({ s, i })).filter(({ s, i }) => s.step <= m.tally && !fired.has(i)).sort((a, b) => a.s.step - b.s.step || a.i - b.i).map(({ i }) => i);
}
/**
 * Whether a host fits the table's system (`features.combat.matrix`), or the refusal in words: its code one of the
 * system's, a rating for every subsystem and no other, its alert one of the system's.
 */
export function hostRefusal(h, rules) {
    if (!rules.codes.some((c) => c.key === h.code))
        return 'That is not one of this game system\'s security codes.';
    const keys = Object.keys(h.ratings);
    const lacking = rules.subsystems.find((s) => h.ratings[s.key] === undefined);
    if (lacking)
        return `The host needs a ${lacking.label} rating.`;
    if (keys.some((k) => !rules.subsystems.some((s) => s.key === k)))
        return 'That is not one of this game system\'s subsystems.';
    if (h.alert !== null && !rules.alerts.some((a) => a.key === h.alert))
        return 'That is not one of this game system\'s alerts.';
    if (h.sheaf?.some((s) => s.alert !== undefined && !rules.alerts.some((a) => a.key === s.alert)))
        return 'A step of the sheaf names an alert that is not one of this game system\'s.';
    if (h.sheaf?.some((s) => s.ic !== undefined) && !rules.sheaf)
        return "This table's game system has no security sheaf.";
    if (rules.sheaf && h.sheaf?.some((s) => s.ic !== undefined && s.ic.split('/')[0] !== rules.sheaf.family))
        return 'A step of the sheaf names something that is not IC.';
    return null;
}
//# sourceMappingURL=matrixHost.js.map